export const DOLFIN_ACCENT = "#2DD4BF";

export type Rgb = { r: number; g: number; b: number };

// Accepts "#rgb" or "#rrggbb" (case-insensitive). Returns null for anything else —
// callers should fall back to DOLFIN_ACCENT.
export function parseBrandColour(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) return null;
  if (trimmed.length === 4) {
    const [_, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return trimmed.toLowerCase();
}

export function hexToRgb(hex: string): Rgb | null {
  const parsed = parseBrandColour(hex);
  if (!parsed) return null;
  return {
    r: parseInt(parsed.slice(1, 3), 16),
    g: parseInt(parsed.slice(3, 5), 16),
    b: parseInt(parsed.slice(5, 7), 16),
  };
}

// Relative luminance (0–1). Uses simple coefficients — close enough for
// "pick a legible foreground" decisions without reaching for a colour library.
export function luminance(rgb: Rgb): number {
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

export function pickForeground(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#ffffff";
  return luminance(rgb) > 0.6 ? "#0a0a0a" : "#ffffff";
}

// Is this brand colour dark enough to use directly for foreground text
// against a white card? Guardrail for A5 (amount-due tint).
export function isDarkEnoughForText(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return luminance(rgb) < 0.55;
}

export function tintFor(hex: string, alpha: number = 0.06): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "transparent";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// Pull the first one or two initials from an organisation name. Strips
// common suffixes so "ACME Electric Ltd" becomes "AE", not "AEL".
export function initialsFrom(name: string): string {
  const stripped = name
    .replace(/\b(ltd|limited|inc|incorporated|llc|plc|gmbh|co|company|corp|corporation)\.?\b/gi, "")
    .trim();
  const parts = stripped.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
